import {
  assertSafeInteger,
  EMPTY_JEWEL_SOCKET_PROFILE,
  encodeHealthMilli,
  type JewelSocketProfile,
  jewelSocketProfileSchema,
  PVP_PROFILE_LIMITS,
  type PvpCombatProfileWire,
  parseJewelSocketProfile,
} from '@kenocyber/shared';
import { shipBaseDamageMilli } from '../damage/base';
import { BPS_ONE, primordialHeartBps, shotDamageMilli } from '../damage/exact';
import { resolveJewelEffects } from '../damage/jewel-effects';
import { composeFireIntervalMilliTicks, MILLI_TICKS_PER_TICK } from '../hull/attack-speed';
import { composeMoveSpeedBps } from '../hull/move-speed';
import { hullRadiusFromScaleBps, hullScaleBps, hullScaleFactor } from '../hull/scale';
import {
  PVP_BASE_SPEED_BPS,
  PVP_FIRE_INTERVAL_TICKS,
  PVP_MAX_HEALTH,
  PVP_MAX_HEALTH_MILLI,
  PVP_PROJECTILE_DAMAGE,
  PVP_SHIP_RADIUS_MU,
} from './balance';
import { divTrunc } from './vectors';

/**
 * What one ship IS, as five numbers, carried per participant.
 *
 * ## Why this type exists before anything can change it
 *
 * Every one of these four was a module-level constant read straight out of
 * `balance.ts` by the simulation: `PVP_PROJECTILE_DAMAGE` inside `fire()`,
 * `PVP_MAX_HEALTH` inside `spawnShip` and inside both decoders,
 * `PVP_BASE_SPEED_BPS` inside `moveShip`, and `PVP_FIRE_INTERVAL_TICKS` as the
 * cooldown reload. A constant read at the point of use cannot differ between
 * two ships in the same room, which is exactly the property that has to break
 * before a jewel can exist - and breaking it later, in the same change that
 * introduces the first jewel, would mean one commit that both restructures the
 * simulation and alters its outcome, with nothing to compare against.
 *
 * So the restructuring happens here, alone, with the defaults set to the values
 * that were already being read. **Every duel plays out identically**, and
 * `duel-invariance.test.ts` is what says so rather than this comment.
 *
 * ## What a profile is NOT
 *
 * It is not a loadout, an inventory, an account or a jewel. Nothing in this
 * module reads a database, a socket, a skin or an item, and there is no field
 * any of those could be written into. A profile is five exact integers and the
 * rule that they are integers.
 *
 * O quinto chegou no gate V28F.B e é o único sem tecto: a contagem de Corações
 * Primordiais incorporados na nave. Continua a não ser um item nem um
 * inventário - é um NÚMERO que o servidor resolveu, e quem o resolveu vive fora
 * deste ficheiro, exatamente como os outros quatro.
 */
export interface PvpCombatProfile {
  /**
   * Whole damage of one ordinary projectile, before any rune.
   *
   * The `SHIP_BASE_DAMAGE` of `docs/gates/v28d2-jewel-mechanics-contract.md`,
   * in the unit the PvP already uses. It is the value a jewel's coefficient
   * multiplies, and it is deliberately the value BEFORE the double-damage rune:
   * a rune is a temporary multiplier, and a base that moved with it would make
   * every jewel that reads it read a buff instead.
   */
  readonly baseDamage: number;
  /**
   * **O teto deste casco, JÁ COMPOSTO.** Health is clamped to it and never
   * exceeds it.
   *
   * ## O que ele é, exatamente (gate V28G.S.1)
   *
   * **A vida máxima autoritativa da CONTA de quem se sentou nesta cadeira, em
   * MILLI**, e não um número do duelo. O mesmo `bigint` que a caça usa, ao
   * milésimo: a vida-base da nave, os pontos alocados, a plana dos artefactos e
   * do equipamento, e a soma das percentagens - o Coração do Titã incluído -
   * aplicada uma única vez, por `hunt/account-max-health.ts`.
   *
   * Uma conta nova entra no duelo com 100 000 milli. Uma com 10 000 de vida
   * entra com 10 000 000, e com o Coração do Titã ao nível 30 entra com
   * 16 000 000. Não há conversão, normalização nem multiplicador entre os modos.
   *
   * ## Porque MILLI, e porque `bigint`
   *
   * Porque nenhuma fonte legal produz pontos inteiros: o `flatMaxHealth` de um
   * componente é sorteado por milli e o `maxHealthPercent` por bps, e um Mother
   * Ship nível 1 sozinho vale 108,12 de vida. Este campo era um `number` de
   * pontos e truncava até 0,999 do que a caça tinha - a auditoria do V28G.S
   * mediu-o, e o V28G.S.1 é a correção.
   *
   * `bigint` porque a progressão não tem tecto: 2^40 pontos valem 1,1 x 10^16
   * milli, acima de `Number.MAX_SAFE_INTEGER`.
   *
   * ## Ele chega pronto, e nada neste pacote o compõe
   *
   * Resolvido na emissão do bilhete, que é o último instante com uma base de
   * dados aberta, e transportado dentro do payload assinado. `pvp/` não tem
   * aritmética que multiplique vida por percentagem: é isso que torna a dupla
   * aplicação do Coração do Titã impossível em vez de improvável.
   *
   * Escrito num sítio só - {@link PvpSimulation.freezeParticipantMaxHealth} -
   * quando o piloto se senta. Daí em diante é o número que o instantâneo valida
   * `health` contra e que a métrica do casco mede.
   *
   * ## O que ele NÃO é
   *
   * Não é a vida ATUAL de nada. Os dois modos partilham o máximo e nunca a vida
   * corrente: entrar no duelo é entrar cheio, e uma nave ferida na caça não
   * entra ferida aqui.
   *
   * E não é a vida-base. Um leitor que quisesse a base teria de a inventar, e é
   * deliberado que não possa: o que decide o futuro de uma partida é o teto, e
   * duas leituras diferentes dele seriam duas partidas.
   */
  readonly maxHealthMilli: bigint;
  /**
   * Movement speed as integer basis points of {@link PVP_BASE_SPEED_BPS}.
   *
   * The same unit `pvpAdvanceShip` already takes, so nothing about the physics
   * changes - only where the number comes from.
   */
  readonly speedBps: number;
  /**
   * Ticks between volleys, in MILLI-ticks: 1000 milli-ticks is one tick.
   *
   * ## Why a whole tick could not stay
   *
   * The cooldown was an integer count of ticks, and an integer count cannot
   * express a percentage. `PVP_FIRE_INTERVAL_TICKS` is 4; a cadence bonus of
   * +9 % asks for 3,67, and truncating that to 3 is +33 % - a jewel that
   * granted four times what it says. Milli-ticks give the accumulator somewhere
   * to keep the remainder, which is the same answer the hunt's 16 ms integer
   * accumulator already gives to the same question.
   *
   * The default is exactly `4 x 1000`, so the shipped cadence is unchanged to
   * the tick.
   */
  readonly fireIntervalMilliTicks: number;
  /**
   * Corações Primordiais incorporados na nave que este casco representa.
   *
   * ## Porque é um `bigint` ao lado de quatro `number`
   *
   * Porque é o único campo do perfil que o produto decidiu não ter tecto de
   * jogo. Os outros quatro são propriedades do casco e vivem dentro de
   * `PVP_PROFILE_LIMITS`; este é uma CONTAGEM guardada em `numeric(40, 0)`, e
   * um `number` perderia dígitos a partir de 2^53 - o que seria um teto de item
   * escrito num tipo, exatamente o que o `CLAUDE.md` proíbe.
   *
   * Ele nunca é multiplicado por outro campo daqui. Entra em
   * `primordialHeartBps` e sai como uma parcela somada às percentagens do tiro
   * normal, que é o que impede um coração de multiplicar uma joia futura.
   *
   * ## O que ele NÃO é
   *
   * Não é uma contagem de Corações POSSUÍDOS. É o que está incorporado na nave
   * equipada, lido de `ship_instances.primordial_hearts` no momento em que o
   * bilhete é emitido e congelado aqui para a partida inteira.
   */
  readonly primordialHeartCount: bigint;
  /**
   * As joias de socket deste casco, agregadas (gate V28G.A).
   *
   * ## Porque aqui e não num canal paralelo
   *
   * Porque tudo o que este campo precisa já existe neste tipo e está provado:
   * congela quando o piloto se senta, persiste no documento da sala, é copiado
   * por valor para os seis clientes, e um casco malformado é recusado pelo
   * nome. Um segundo canal teria de repetir as quatro coisas, e a primeira a
   * divergir seria aquela em que ninguém tocou.
   *
   * ## O que ele NÃO é
   *
   * Não é um inventário. Não há UUID de instância, de skin, de socket nem de
   * conta - `JewelSocketProfile` não tem campo onde um caiba, o que é uma
   * garantia mais forte do que a disciplina de não o preencher. O que atravessa
   * é quantas cópias de cada definição e que níveis somados.
   *
   * ## O que ele faz, desde o gate V28G.B
   *
   * Quatro coisas aqui, e todas através de `resolveJewelEffects`: a Chama da
   * Destruição soma ao tiro normal, o Pulso do Dragão encurta o intervalo entre
   * salvas, o Orbe Vampírico devolve parte do dano aplicado e a Égide Esmeralda
   * reduz o dano recebido.
   *
   * O **Coração do Titã** já não é uma delas, e a ausência é a lei do V28G.S:
   * ele soma às percentagens que formam a vida máxima da CONTA, resolvida pela
   * autoridade antes de o bilhete ser assinado. Este campo continua a
   * transportá-lo - a etiqueta e a Visão Geral leem-no daqui - mas nada nesta
   * sala o volta a aplicar. Um casco que compusesse a partir das suas próprias
   * joias transformaria 16 000 em 25 600.
   */
  readonly jewels: JewelSocketProfile;
}

/**
 * 1000 milli-ticks is one tick. The accumulator's unit, named once.
 *
 * The neutral constant, re-exported under the duel's name so every call site
 * stays as it was. Naming it twice would be two units for one fact, and the
 * open world's room counts volleys in exactly this one.
 */
export const PVP_MILLI_TICKS_PER_TICK = MILLI_TICKS_PER_TICK;

/**
 * What every ship in every match has today, and what a duel without jewels will
 * always have.
 *
 * Derived from the balance constants rather than repeated as literals, so a
 * change to the arena's numbers moves the default with it instead of leaving a
 * second copy behind. `combat-profile.test.ts` asserts the equality in the
 * other direction too - that the default still IS the shipped behaviour.
 */
export const PVP_DEFAULT_COMBAT_PROFILE: PvpCombatProfile = Object.freeze({
  baseDamage: PVP_PROJECTILE_DAMAGE,
  maxHealthMilli: PVP_MAX_HEALTH_MILLI,
  speedBps: PVP_BASE_SPEED_BPS,
  fireIntervalMilliTicks: PVP_FIRE_INTERVAL_TICKS * PVP_MILLI_TICKS_PER_TICK,
  /*
   * Zero, e é a razão pela qual um duelo sem corações continua idêntico.
   *
   * `0n` faz `primordialHeartBps` devolver `0n`, e `shotDamageMilli(base, 0n)` é
   * `base` exatamente - sem divisão que arredonde e sem ramo especial. Um casco
   * que nunca ouviu falar de corações joga o duelo que sempre jogou, o que
   * `duel-invariance.test.ts` afirma em vez de este comentário.
   */
  primordialHeartCount: 0n,
  /*
   * Vazio, e é a razão pela qual um duelo sem joias continua idêntico.
   *
   * Partilhado em vez de reconstruído: a ausência de joias é o estado de todas
   * as contas que existem hoje, e um objeto novo por cadeira seria alocação
   * para descrever a mesma ausência seis vezes.
   */
  jewels: EMPTY_JEWEL_SOCKET_PROFILE,
});

/**
 * Validates a profile, or throws.
 *
 * Every field is a positive safe integer. Zero is refused for all four and each
 * refusal is a different impossible match: no damage is a gun that cannot kill,
 * no health is a hull that is already dead, no speed is a ship nailed to its
 * spawn, and no interval is an infinite volley in one tick.
 */
export function pvpAssertCombatProfile(
  profile: PvpCombatProfile,
  what = 'pvp combat profile',
): void {
  assertSafeInteger(profile.baseDamage, `${what}.baseDamage`);

  assertSafeInteger(profile.speedBps, `${what}.speedBps`);
  assertSafeInteger(profile.fireIntervalMilliTicks, `${what}.fireIntervalMilliTicks`);
  if (profile.baseDamage < 1 || profile.baseDamage > PVP_PROFILE_LIMITS.maxBaseDamage) {
    throw new RangeError(`${what}.baseDamage is outside its bounds: ${profile.baseDamage}`);
  }
  /*
   * A vida máxima é validada por TIPO e por SINAL, e por nada mais (V28G.S.1).
   *
   * Sem tecto, exatamente como a contagem de Corações: ela é a vida máxima da
   * CONTA e a progressão não tem limite, portanto um máximo aqui seria um teto
   * de progressão escrito num validador. O limite que existe é o de PAYLOAD, e
   * é verificado onde a vida atravessa um fio.
   *
   * O mínimo é um milésimo: um casco sem vida nenhuma já está morto, e nascer
   * morto não é um casco.
   */
  if (typeof profile.maxHealthMilli !== 'bigint') {
    throw new RangeError(`${what}.maxHealthMilli must be a bigint`);
  }
  if (profile.maxHealthMilli < 1n) {
    throw new RangeError(`${what}.maxHealthMilli is below one milli: ${profile.maxHealthMilli}`);
  }
  if (profile.speedBps < 1 || profile.speedBps > PVP_PROFILE_LIMITS.maxSpeedBps) {
    throw new RangeError(`${what}.speedBps is outside its bounds: ${profile.speedBps}`);
  }
  if (
    profile.fireIntervalMilliTicks < 1 ||
    profile.fireIntervalMilliTicks > PVP_PROFILE_LIMITS.maxFireIntervalMilliTicks
  ) {
    throw new RangeError(
      `${what}.fireIntervalMilliTicks is outside its bounds: ${profile.fireIntervalMilliTicks}`,
    );
  }
  /*
   * A contagem de corações é validada por TIPO e por SINAL, e por nada mais.
   *
   * Não há tecto aqui, e a ausência é a decisão: os outros quatro campos têm um
   * limite de REPRESENTAÇÃO porque um casco malformado tem de ser recusado pelo
   * nome, e este não pode ter um porque um limite superior seria um tecto de
   * jogo. O limite que existe é o do armazenamento - 40 dígitos, em
   * `PRIMORDIAL_HEART_MAX_COUNT` - e é verificado onde a contagem NASCE, na
   * incorporação, e não onde ela é lida.
   *
   * Zero é legal, ao contrário dos outros quatro: um casco sem corações é o
   * casco normal, e é o que quase todos os duelos usam.
   */
  if (typeof profile.primordialHeartCount !== 'bigint') {
    throw new RangeError(`${what}.primordialHeartCount must be a bigint`);
  }
  if (profile.primordialHeartCount < 0n) {
    throw new RangeError(
      `${what}.primordialHeartCount is never negative: ${profile.primordialHeartCount}`,
    );
  }
  /*
   * O perfil de joias é validado pelo CONTRATO, e não por um segundo conjunto
   * de regras escrito aqui.
   *
   * `jewelSocketProfileSchema` já sabe o que é legal: as catorze identidades
   * derivadas do catálogo, o tecto de seis assentos, `copies <= levelSum <=
   * copies x cap`, a ordem canónica, nenhuma chave desconhecida e nunca a peça
   * soberana. Repetir qualquer dessas regras aqui criaria um segundo sítio onde
   * "que joias são legais" pode ser respondido, e duas respostas para essa
   * pergunta é exatamente o que a fronteira existe para evitar.
   */
  const jewels = jewelSocketProfileSchema.safeParse(profile.jewels);
  if (!jewels.success) {
    throw new RangeError(`${what}.jewels is not a legal socket profile: ${jewels.error.message}`);
  }
}

/** Field-by-field equality. Used where a format can only carry the default. */
export function pvpIsDefaultCombatProfile(profile: PvpCombatProfile): boolean {
  return (
    profile.baseDamage === PVP_DEFAULT_COMBAT_PROFILE.baseDamage &&
    profile.maxHealthMilli === PVP_DEFAULT_COMBAT_PROFILE.maxHealthMilli &&
    profile.speedBps === PVP_DEFAULT_COMBAT_PROFILE.speedBps &&
    profile.fireIntervalMilliTicks === PVP_DEFAULT_COMBAT_PROFILE.fireIntervalMilliTicks &&
    profile.primordialHeartCount === PVP_DEFAULT_COMBAT_PROFILE.primordialHeartCount &&
    profile.jewels.entries.length === 0
  );
}

/** A defensive copy. Profiles are plain data and never shared with a snapshot. */
export function pvpCopyCombatProfile(profile: PvpCombatProfile): PvpCombatProfile {
  return {
    baseDamage: profile.baseDamage,
    maxHealthMilli: profile.maxHealthMilli,
    speedBps: profile.speedBps,
    fireIntervalMilliTicks: profile.fireIntervalMilliTicks,
    primordialHeartCount: profile.primordialHeartCount,
    /*
     * O perfil de joias é reconstruído entrada a entrada, como tudo o resto.
     *
     * Um perfil entregue por referência seria um objeto que seis quadros podem
     * ver as edições uns dos outros - a mesma classe de erro que
     * documenta para os seus arrays.
     */
    jewels: { entries: profile.jewels.entries.map((entry) => ({ ...entry })) },
  };
}

/* -------------------------------------------------------------------------- */
/*  O casco: quanto ele aguenta, e quanto ele mede                            */
/* -------------------------------------------------------------------------- */

/*
 * **Não há aqui uma composição de vida máxima, e a ausência é a lei** (V28G.S).
 *
 * O V28G.R tinha uma - `pvpComposeMaxHealth(flatMaxHealth, jewels)` - porque a
 * vida-base do duelo era a constante normalizada de 1 000 e o Coração do Titã
 * era a única fonte que existia. O V28G.S trocou essa FONTE pela vida máxima
 * autoritativa da conta, que chega ao duelo **já composta**: a vida-base, os
 * pontos alocados, a plana dos artefactos e do equipamento, e a soma das
 * percentagens - o Coração do Titã incluído - aplicada uma única vez, em
 * `hunt/account-max-health.ts`.
 *
 * Manter a função seria manter um caminho por onde 16 000 vira 25 600. Ela foi
 * REMOVIDA, e com ela toda a aritmética de vida-por-percentagem deste módulo:
 * nada em `pvp/` multiplica um teto por uma percentagem, e é por construção -
 * não por disciplina - que o Coração do Titã não pode ser aplicado duas vezes.
 *
 * `profile.maxHealth` é o teto final e nada mais o toca. Quem o escreve é
 * `PvpSimulation.freezeParticipantMaxHealth`, uma vez, quando o piloto se senta.
 */

/**
 * **A métrica canónica deste casco, em basis points.**
 *
 * `10 000` é a nave de referência - a que tem exatamente {@link PVP_MAX_HEALTH}
 * de vida máxima - e é o que todo o casco sem Coração do Titã produz,
 * exatamente. Acima disso cresce com a raiz da vida máxima FINAL.
 *
 * É a ÚNICA fonte de tamanho de um casco de duelo. O sprite, a hitbox, o raio
 * autoritativo, a colisão de um projétil, a de um míssil, a proximidade de uma
 * bomba, o contacto e a órbita das serras, o ponto onde um tiro nasce, as
 * paredes, a barra, o marcador e as marcas saem todos daqui - nenhum deles
 * recalcula a curva, e é isso que impede a aparência de crescer sem a física.
 */
export function pvpHullScaleBps(profile: Pick<PvpCombatProfile, 'maxHealthMilli'>): bigint {
  return hullScaleBps(profile.maxHealthMilli);
}

/**
 * O raio autoritativo deste casco, em `mu` inteiros.
 *
 * `PVP_SHIP_RADIUS_MU` para todo o casco de vida normal, ao `mu`, porque a
 * escala é exatamente `10 000` e a função devolve a base sem aritmética
 * nenhuma. É esse degrau zero que faz um duelo sem joias jogar-se tique a tique
 * como sempre se jogou.
 */
export function pvpHullRadiusMu(profile: Pick<PvpCombatProfile, 'maxHealthMilli'>): number {
  return hullRadiusFromScaleBps(PVP_SHIP_RADIUS_MU, pvpHullScaleBps(profile));
}

/** A mesma escala como fator, para quem desenha. Nunca uma segunda curva. */
export function pvpHullScale(profile: Pick<PvpCombatProfile, 'maxHealthMilli'>): number {
  return hullScaleFactor(pvpHullScaleBps(profile));
}

/**
 * How fast this hull actually moves, this tick.
 *
 * ## Why the rune is composed rather than substituted
 *
 * It used to be a substitution: `speedTicks > 0 ? PVP_RUNE_SPEED_BPS :
 * PVP_BASE_SPEED_BPS`. That reads "while the rune is up, the ship's speed IS
 * 15 000" - which silently discards whatever the hull's own speed was, and
 * would have made a movement jewel worth nothing for the nine seconds a player
 * most wants it.
 *
 * So the rune became a ratio applied to the profile. With the default profile
 * the two are the same number to the unit - `10 000 x 15 000 / 10 000` is
 * `15 000` - which is what keeps this gate's duels identical.
 *
 * Integer throughout, and truncating rather than rounding, because the client
 * replays this exact function: a float here would put the room and the
 * prediction one bit apart and correct forever.
 */
/**
 * **A velocidade PRÓPRIA deste casco, em basis points, com as joias somadas.**
 *
 * ## O agrupamento percentual do movimento, e é este
 *
 * `profile.speedBps` é a velocidade do casco em basis points da base - 10 000 é
 * a nave de referência, e um casco com um bónus próprio de +20 % chega aqui com
 * 12 000. O Passo Alado é uma PARCELA nesse mesmo número:
 *
 *     10 000 + bonusProprioBps + passoAladoBps
 *
 * e nunca `12 000 x 13 900 / 10 000`. Um casco com +20 % próprios e a joia ao
 * nível 30 anda a `1,59x`, e não a `1,668x` - é a mesma lei aditiva do contrato
 * V28D.2 que já governa o dano, a cadência e a vida máxima, e é a razão pela qual
 * somar existe em vez de encadear multiplicações.
 *
 * ## Porque a soma acontece AQUI e não no perfil
 *
 * Porque `profile.speedBps` é um campo do FIO e do documento da sala, comparado
 * contra {@link PVP_DEFAULT_COMBAT_PROFILE} e persistido tal como chega. Escrever
 * a joia lá dentro faria uma sala sem joias deixar de ser byte a byte a sala de
 * sempre, e faria o mesmo número significar duas coisas conforme quem o leu. A
 * composição é derivada em cada leitura, exatamente como
 * {@link pvpFireIntervalMilliTicks} deriva a cadência.
 *
 * ## A lei é PARTILHADA com a caça, e é por isso que ela é uma travessia
 *
 * A soma vive em `hull/move-speed.ts`, que a caça também atravessa. Escrever
 * `profile.speedBps + Number(bps)` aqui daria o mesmo número hoje e seria uma
 * segunda expressão da mesma lei - e a primeira a divergir seria aquela em que
 * ninguém tocou. É a mesma razão pela qual a vida máxima dos dois modos sai de
 * `hull/max-health.ts`.
 *
 * Daí vem também a IDENTIDADE: sem a joia, a composição devolve
 * `profile.speedBps` sem tocar em aritmética nenhuma. Não é uma otimização - é
 * o que garante que a velocidade é o MESMO valor, e não um valor que calhou dar
 * o mesmo.
 */
export function pvpHullSpeedBps(profile: Pick<PvpCombatProfile, 'speedBps' | 'jewels'>): number {
  return composeMoveSpeedBps(profile.speedBps, resolveJewelEffects(profile.jewels).moveSpeedBps);
}

export function pvpEffectiveSpeedBps(
  /*
   * Os dois campos que importam, e é uma decisão em vez de uma conveniência.
   *
   * Esta função lê `speedBps` e `jewels`, e nada mais. Aceitar o perfil inteiro
   * obrigava quem só tem a forma do FIO - o cliente, que recebe a contagem de
   * corações como string - a construir um perfil de domínio para chamar uma
   * função que nunca lhe tocaria. Estreitar o parâmetro diz o que ela usa e deixa
   * as duas formas passarem sem conversão; `jewels` tem a mesma forma dos dois
   * lados, por isso entrou sem custo nenhum.
   */
  profile: Pick<PvpCombatProfile, 'speedBps' | 'jewels'>,
  runeSpeedBps: number | null,
): number {
  /*
   * A velocidade própria do casco - joias JÁ somadas - e depois a runa.
   *
   * A ordem é a que o comentário desta função sempre prometeu: a runa é uma
   * RAZÃO aplicada ao casco, e não uma substituição, precisamente para que uma
   * joia de movimento não valha zero durante os nove segundos em que o jogador
   * mais a quer. As percentagens do casco somam-se entre si; a runa, que é um
   * estado temporário e não uma propriedade do casco, multiplica o resultado -
   * exatamente como a runa de Dano Dobrado multiplica um tiro cujas percentagens
   * já foram somadas.
   */
  const hullSpeedBps = pvpHullSpeedBps(profile);
  if (runeSpeedBps === null) {
    return hullSpeedBps;
  }
  return divTrunc(hullSpeedBps * runeSpeedBps, PVP_BASE_SPEED_BPS);
}

/**
 * Quantos milli-tiques esta nave espera entre salvas, com as joias aplicadas.
 *
 * ## A fórmula, e porque não é `base x (1 - bps)`
 *
 * `divTrunc(base x 10000, 10000 + bps)`.
 *
 * A joia promete uma percentagem de TAXA - `+9 %` são nove por cento mais tiros
 * por segundo. Encurtar o intervalo em 9 % daria `base x 0,91`, que é `+9,89 %`
 * de taxa: uma etiqueta a mentir por um ponto percentual inteiro. A recíproca é
 * a única leitura que faz a etiqueta ser verdade.
 *
 * ## Porque o perfil não é reescrito
 *
 * `profile.fireIntervalMilliTicks` continua a ser a cadência-base do casco -
 * 4 000 no perfil por omissão - e esta função compõe as joias por cima, como
 * `pvpEffectiveSpeedBps` compõe a runa em vez de a substituir. Escrever o
 * resultado no perfil faria o campo do fio deixar de ser comparável ao valor por
 * omissão, e uma sala sem joias deixaria de ser byte a byte a sala de sempre.
 *
 * ## O truncamento, e o que NÃO se faz com ele
 *
 * A divisão trunca, o que encurta o intervalo e entrega até **+0,024 pontos
 * percentuais** acima da etiqueta (o pior caso, em `levelSum` 180). Aprovado
 * como está no gate V28G.D: compensar com um tiro extra, um tiro suprimido,
 * uma alternância ou aleatoriedade trocaria um desvio de um quadragésimo de
 * ponto percentual por uma cadência que deixa de ser determinística - e a sala
 * e a re-simulação têm de concordar tique a tique.
 *
 * Nunca arredonda para tiques inteiros. Truncar `3 988` para `3` tiques daria
 * `+33,3 %` onde a etiqueta promete `+0,30 %`, e é por isso que o intervalo é
 * contado em milli-tiques.
 *
 * ## Aritmética em `bigint`
 *
 * Porque `attackSpeedBps` chega como `bigint` do resolvedor, que o mantém assim
 * por causa das grandezas sem tecto. O produto `4 000 x 10 000` cabe folgado num
 * `number`, mas converter o bps para `number` antes de somar seria um sítio onde
 * um perfil absurdo perderia dígitos em silêncio. O quociente é sempre menor ou
 * igual à base, portanto o `Number` final é exato.
 */
export function pvpFireIntervalMilliTicks(
  profile: Pick<PvpCombatProfile, 'fireIntervalMilliTicks' | 'jewels'>,
): number {
  /*
   * A LEI vive em `hull/attack-speed.ts`, e é a mesma para os dois modos.
   *
   * Ela era escrita aqui, e a única coisa que a prendia ao duelo era o sítio
   * onde estava: a recíproca, o piso de um milli-tique, a identidade sem joia e
   * a aritmética em `bigint` são todos idênticos. A sala do mundo aberto conta
   * salvas da mesma maneira, e uma segunda cópia seria a primeira a divergir.
   *
   * O que fica aqui é o que é do duelo: qual perfil, e que joias.
   */
  return composeFireIntervalMilliTicks(
    profile.fireIntervalMilliTicks,
    resolveJewelEffects(profile.jewels).attackSpeedBps,
  );
}

/**
 * `SHIP_BASE_DAMAGE_MILLI` for a duel: exact, integer, and the same MEANING the
 * hunt's has - one ordinary projectile, before anything.
 *
 * The second argument is `0n` and that is a statement rather than a placeholder:
 * a duel normalises equipment away, so there is no flat base attack to add. The
 * hunt passes its components' `baseAttackMilli` into the same function
 * (`huntShipBaseDamageMilli`), which is what makes "the same meaning in both
 * modes" a shared line of arithmetic instead of a shared paragraph.
 */
export function pvpShipBaseDamageMilli(profile: PvpCombatProfile): number {
  return shipBaseDamageMilli(profile.baseDamage, 0n);
}

/**
 * What one projectile takes off, decided when it is FIRED.
 *
 * The double-damage rune multiplies the profile's base, in basis points, with
 * one truncation at the end - the same arithmetic `fire()` already did, with
 * the constant replaced by the profile.
 */
export function pvpShotDamage(profile: PvpCombatProfile, damageBps: number): number {
  return divTrunc(profile.baseDamage * damageBps, 10_000);
}

/**
 * **What one projectile takes off, in MILLI-damage and exactly.**
 *
 * The function {@link pvpShotDamage} should have been from the start, and the
 * one `fire()` now calls. Three factors, one truncation at the end:
 *
 *     base_milli x (10 000 + 100 x hearts) x rune_bps / (10 000 x 10 000)
 *
 * ## Why the hearts are ADDITIVE and the rune is a MULTIPLIER
 *
 * Because they are different kinds of thing, and the difference is the whole
 * anti-feedback rule.
 *
 * A heart is a percentage of the ordinary shot, in the same summed bucket the
 * ship's own bonus, the artifacts and a future Chama da Destruição share. It
 * goes through {@link primordialHeartBps} into `shotDamageMilli`'s single
 * addend, so `+900` next to `+2 500` is `+3 400` and never `1,09 x 1,25` -
 * which is precisely what stops a heart multiplying a jewel and a jewel
 * multiplying a heart.
 *
 * A rune is not a percentage of the shot: it is a temporary DOUBLING, and
 * `combat-profile.ts` has said so since it was written - `baseDamage` is
 * "deliberately the value BEFORE the double-damage rune". Folding it into the
 * additive bucket would make the rune worth `+100 % of base` instead of
 * `x2 of the shot`, so a player with a hundred hearts would find their rune had
 * quietly become half as good. It stays a factor.
 *
 * ## One truncation, and duel invariance
 *
 * With no hearts the middle factor is exactly `10 000` and the expression
 * reduces to `base_milli x rune_bps / 10 000`, which is `pvpShotDamage`'s
 * arithmetic scaled by a thousand - `40 -> 40 000`, `80 -> 80 000`. Both shipped
 * rune values divide exactly, so a duel without hearts plays out to the tick as
 * it always did; `duel-invariance.test.ts` is what says so.
 *
 * ## Never converted
 *
 * The result stays a `bigint` all the way to {@link resolveIntegerStrike}, which
 * decides lethality by comparing rather than converting. A hundred-heart shot
 * and a `10^40`-heart shot are the same `bigint` arithmetic, and neither ever
 * has to fit in a `double`.
 */
export function pvpShotDamageMilli(profile: PvpCombatProfile, runeDamageBps: number): bigint {
  const baseMilli = BigInt(pvpShipBaseDamageMilli(profile));
  /*
   * As joias entram na MESMA parcela que os corações (gate V28G.B).
   *
   * Uma soma de basis points, e não um segundo `shotDamageMilli` encadeado:
   * `base x (1 + p + k)` e não `base x (1 + p) x (1 + k)`. Dez corações e uma
   * Chama de nível 30 valem `+1 900` e nunca `1,10 x 1,09` - que é precisamente
   * a realimentação que a lei aditiva do contrato V28D.2 proíbe.
   *
   * `resolveJewelEffects` é quem sabe o que cada joia vale; esta linha soma um
   * `bigint` e não conhece nenhuma delas pelo nome.
   */
  const summedBps =
    primordialHeartBps(profile.primordialHeartCount) +
    resolveJewelEffects(profile.jewels).damageBps;
  const withHearts = shotDamageMilli(baseMilli, summedBps);
  /*
   * A segunda divisão é a da runa, e é a única. `shotDamageMilli` já truncou a
   * primeira - e com zero corações não há primeira, porque `x 10 000 / 10 000`
   * é a identidade sobre inteiros.
   */
  return (withHearts * BigInt(runeDamageBps)) / BPS_ONE;
}

/**
 * The running profile as a FRAME carries it.
 *
 * One field crosses a representation boundary and the rest are copies: the
 * heart count is a `bigint` in a simulation that must not lose a digit of it,
 * and a decimal string on a wire that is JSON and has no `bigint`.
 *
 * Explicit, and a function rather than a spread, for the reason every crossing
 * in this project is: the two shapes were structurally identical until the gate
 * V28F.B, so a spread compiled and would have gone on compiling right up to the
 * moment it silently serialised a `bigint` into something `JSON.stringify`
 * refuses.
 */
export function pvpCombatProfileToWire(profile: PvpCombatProfile): PvpCombatProfileWire {
  return {
    baseDamage: profile.baseDamage,
    maxHealthMilli: encodeHealthMilli(profile.maxHealthMilli),
    speedBps: profile.speedBps,
    fireIntervalMilliTicks: profile.fireIntervalMilliTicks,
    primordialHeartCount: profile.primordialHeartCount.toString(10),
    jewels: { entries: profile.jewels.entries.map((entry) => ({ ...entry })) },
  };
}

/**
 * And back, for the client that predicts with what the room told it.
 *
 * The schema has already proved the string is a canonical decimal, so `BigInt`
 * cannot throw here - and it is deliberately not wrapped in a try: a value that
 * reached this function without being parsed is a bug in the caller, and
 * swallowing it would hand the predictor a hull that is not the room's.
 */
export function pvpCombatProfileFromWire(profile: PvpCombatProfileWire): PvpCombatProfile {
  return {
    baseDamage: profile.baseDamage,
    maxHealthMilli: BigInt(profile.maxHealthMilli),
    speedBps: profile.speedBps,
    fireIntervalMilliTicks: profile.fireIntervalMilliTicks,
    primordialHeartCount: BigInt(profile.primordialHeartCount),
    /*
     * Validado ao voltar, e não apenas copiado.
     *
     * O esquema do fio já provou a forma, mas este é o ponto onde um perfil
     * entra no PREDITOR do cliente, e um preditor que aceitasse um casco que a
     * sala nunca declarou divergiria dela para sempre.
     */
    jewels: parseJewelSocketProfile(profile.jewels),
  };
}
